import {useEffect, useRef, useState} from 'react';
import './FenzSafePacketPrintPreview.css';
import {groupDocumentsByCategory, Matter} from './fenzsafeStore';

type Props = {
  matter: Matter;
  /** 'draft' is the customer previewing before anything is shared; 'shared' is the attorney's own copy of a package that has actually gone out. */
  variant: 'draft' | 'shared';
  onClose: () => void;
};

/**
 * Sequential exhibit numbering across the whole packet, in the exact order documents are
 * rendered below (category order, then each category's own document order) -- a purely
 * organizational device (letting a reader say "see Exhibit 003") with no claim about a
 * document's admissibility or authenticity attached to it. Assigned to every document in the
 * packet regardless of whether it has a real hash yet, unlike the package-root hash below.
 */
function batesNumbersByDocumentId(groups: {documents: {id: string}[]}[]): Map<string, string> {
  const numbers = new Map<string, string>();
  let n = 0;
  for (const group of groups) {
    for (const doc of group.documents) {
      n += 1;
      numbers.set(doc.id, String(n).padStart(3, '0'));
    }
  }
  return numbers;
}

/**
 * A single hash standing for "this exact set of documents, in this exact order, each with this
 * exact content" -- SHA-256 of the ordered concatenation of each document's own already-verified
 * content_sha256 (real hashes only, computed server-side at storage-accept time; see
 * DocumentController::contentSha256()'s doc comment). Deliberately returns null rather than a
 * partial hash whenever any document in the packet lacks a real hash -- a root hash that silently
 * covered only some of the documents would misleadingly read as covering the whole packet.
 */
async function packageRootHash(orderedContentHashes: (string | undefined)[]): Promise<string | null> {
  if (orderedContentHashes.length === 0 || orderedContentHashes.some((hash) => !hash)) return null;
  const bytes = new TextEncoder().encode(orderedContentHashes.join(''));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Printable/savable summary of a matter's package -- same content the attorney sees on-screen,
 * as an actual page a customer or attorney can print or save as a PDF. Reuses the
 * AffidavitPrintPreview.tsx overlay/print pattern (window.print() + @media print visibility
 * toggle) rather than inventing a second one.
 *
 * Unlike AffidavitPrintPreview.tsx, this is a full-screen overlay that traps focus and closes
 * on Escape -- tabbing out of a modal into the (visually hidden) page behind it is a real
 * keyboard-trap-adjacent defect, found via a keyboard audit of this component.
 */
export function FenzSafePacketPrintPreview({matter, variant, onClose}: Props) {
  const groups = groupDocumentsByCategory(matter);
  const overlayRef = useRef<HTMLDivElement>(null);
  const batesNumbers = batesNumbersByDocumentId(groups);
  const [rootHash, setRootHash] = useState<string | null>(null);

  useEffect(() => {
    overlayRef.current?.querySelector<HTMLButtonElement>('.fppp-secondary')?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const orderedHashes = groups.flatMap((group) => group.documents.map((doc) => doc.contentSha256));
    void packageRootHash(orderedHashes).then((hash) => {
      if (!cancelled) setRootHash(hash);
    });
    return () => {
      cancelled = true;
    };
  }, [matter.documents]);

  function trapFocus(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !overlayRef.current) return;
    const focusable = Array.from(overlayRef.current.querySelectorAll<HTMLElement>('button, a[href]'));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div ref={overlayRef} className="fppp-overlay" role="dialog" aria-label="Package print preview" onKeyDown={trapFocus}>
      <div className="fppp-document">
        <p className="fppp-banner">{variant === 'draft' ? 'DRAFT PREVIEW — NOT YET SHARED' : 'SYNTHETIC PACKAGE — LOCAL PREVIEW ONLY'}</p>
        <div className="fppp-brand" aria-hidden="true">
          FenzSafe
        </div>
        <h2>{matter.name}</h2>
        <dl className="fppp-meta">
          <div>
            <dt>Reference</dt>
            <dd>{matter.reference}</dd>
          </div>
          <div>
            <dt>Prepared</dt>
            <dd>{matter.createdAt}</dd>
          </div>
          <div>
            <dt>Documents</dt>
            <dd>{matter.documents.length}</dd>
          </div>
          <div>
            <dt>Package hash (SHA-256)</dt>
            <dd className="fppp-hash">
              {rootHash ?? 'Not available — one or more documents have no verified hash yet'}
            </dd>
          </div>
        </dl>
        <hr />
        {groups.length === 0 ? (
          <p>No documents in this matter yet.</p>
        ) : (
          groups.map((group) => (
            <div className="fppp-category" key={group.category}>
              <p className="fppp-category-name">
                {group.category} ({group.documents.length})
              </p>
              <ul>
                {group.documents.map((doc) => (
                  <li key={doc.id}>
                    <span className="fppp-exhibit-number">Exhibit {batesNumbers.get(doc.id)}</span> — {doc.name}
                    {doc.docVersion > 1 ? ` — version ${doc.docVersion}` : ''}
                    {doc.contentSha256 && <span className="fppp-hash fppp-doc-hash"> · SHA-256 {doc.contentSha256}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
        <p className="fppp-footer">
          {variant === 'draft'
            ? 'This is a preview of what would be shared. Nothing has been sent to an attorney yet.'
            : 'This copy reflects the package as it was shared. Local synthetic module — no document bytes, provider, or storage is involved.'}
        </p>
        <p className="fppp-footer">
          Exhibit numbers are for reference within this package only. A hash confirms which bytes were stored and does not by itself establish admissibility in any jurisdiction.
        </p>
      </div>
      <div className="fppp-actions fppp-print-hide">
        <button type="button" className="fppp-secondary" onClick={onClose}>
          Close
        </button>
        <button type="button" className="fppp-primary" onClick={() => window.print()}>
          Print / save as PDF
        </button>
      </div>
    </div>
  );
}
